import { GatePassDateTimeFields } from "@/components/sales/GatePassDateTimeFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import type { Attribute, AttributeValue } from "@/lib/catalog/types";
import { ChecklistAttributeField } from "@/components/sales/ChecklistAttributeField";

type GenerateGatePassDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: string;
  time: string;
  onDateChange: (next: string) => void;
  onTimeChange: (next: string) => void;
  saving?: boolean;
  onSubmit: () => void;
  checklistAttributes?: Attribute[];
  checklistValues?: Record<string, AttributeValue>;
  onChecklistValueChange?: (code: string, value: AttributeValue) => void;
};

/** Same Generate Gate Pass modal used on the sale details page. */
export function GenerateGatePassDialog({
  open,
  onOpenChange,
  date,
  time,
  onDateChange,
  onTimeChange,
  saving = false,
  onSubmit,
  checklistAttributes = [],
  checklistValues = {},
  onChecklistValueChange,
}: GenerateGatePassDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (saving) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate Gate Pass</DialogTitle>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <GatePassDateTimeFields
            date={date}
            time={time}
            onDateChange={onDateChange}
            onTimeChange={onTimeChange}
            disabled={saving}
          />

          {checklistAttributes.length > 0 ? (
            <div className="space-y-3 pt-3 border-t border-border/50">
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Delivery Checklist</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {checklistAttributes.map((attr) => (
                  <ChecklistAttributeField
                    key={attr.id}
                    attribute={attr}
                    value={checklistValues[attr.code]}
                    onChange={(v) => onChecklistValueChange?.(attr.code, v)}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter className="border-t border-border/40 pt-3">
          <Button type="button" variant="glass" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="premium" onClick={onSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Generate Gate Pass"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
